/* =============================================================================
   BLUEMINGS — WAITING LIST
   Opens and closes the pop-up, and decides when it raises itself.

   Loaded with `defer` from sections/bluemings-prelaunch-nav.liquid, so the DOM
   is parsed by the time this runs. No dependencies, no library.
   ============================================================================= */

(function () {
  'use strict';

  var modal = document.getElementById('bm-waitlist');
  if (!modal) return;

  var card = modal.querySelector('.bm-wl__card');
  if (!card) return;

  /* The nav wrapper carries `overflow-x: clip` and its own stacking context.
     A fixed child survives that today, but nothing about the pop-up should
     depend on it — move the panel to the body and it is the viewport's. */
  if (modal.parentNode !== document.body) document.body.appendChild(modal);

  var DISMISSED   = 'bluemings_waitlist_dismissed';
  var PENDING     = 'bluemings_waitlist_pending';
  var SUBSCRIBED  = 'bluemings_newsletter_subscribed';   /* written by bluemings-prelaunch.js */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var lastTrigger = null;
  var closeTimer  = null;

  function readFlag(key) {
    try { return window.sessionStorage.getItem(key) === 'true'; } catch (e) { return false; }
  }
  function writeFlag(key, value) {
    try { window.sessionStorage.setItem(key, value ? 'true' : 'false'); } catch (e) { /* private mode */ }
  }
  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function isOpen() {
    return modal.classList.contains('is-open');
  }

  /* ---------------------------------------------------------------------------
     Grow from whatever opened it. The closed state sits at the trigger's offset
     from the centre of the screen, scaled down; opening runs to identity. The
     offset stays on the card, so closing reverses the same path.
     --------------------------------------------------------------------------- */
  function setOrigin(trigger) {
    var dx = 0;
    var dy = 0;

    if (trigger && trigger.getBoundingClientRect && !reducedMotion()) {
      var r = trigger.getBoundingClientRect();
      /* A hidden trigger measures 0×0 at the top-left corner, which would grow
         the card out of the corner of the screen instead of out of the button.
         Fall back to a plain centre scale. */
      if (r.width > 0 && r.height > 0) {
        dx = Math.round((r.left + r.width  / 2) - window.innerWidth  / 2);
        dy = Math.round((r.top  + r.height / 2) - window.innerHeight / 2);
      }
    }

    card.style.setProperty('--bm-wl-dx', dx + 'px');
    card.style.setProperty('--bm-wl-dy', dy + 'px');
  }

  /* The button and the mobile strip are both triggers, but only one of them is
     ever displayed. When the pop-up raises itself there is no click to tell us
     which, so take the first one that is actually on screen. */
  function visibleTrigger() {
    var all = document.querySelectorAll('[data-bm-wl-open]');
    for (var i = 0; i < all.length; i++) {
      var r = all[i].getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return all[i];
    }
    return null;
  }

  function open(trigger) {
    if (isOpen()) return;
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }

    lastTrigger = trigger || null;
    modal.classList.remove('is-closing');
    setOrigin(trigger);

    modal.hidden = false;
    document.body.classList.add('bm-wl-open');

    /* Force a layout so the transition starts from the closed state rather than
       jumping straight to the open one. */
    void card.offsetWidth;

    modal.classList.add('is-open');
    card.focus({ preventScroll: true });
  }

  function close() {
    if (!isOpen()) return;

    /* Closing does not retrace the opening. `.is-closing` pins the card where it
       is and fades it out; the close control animates instead. */
    modal.classList.add('is-closing');
    modal.classList.remove('is-open');
    document.body.classList.remove('bm-wl-open');
    writeFlag(DISMISSED, true);

    /* Keep it in the DOM until the shrink finishes, then take it out of the
       tab order. transitionend can be missed if the tab is backgrounded, so a
       timer backs it up. */
    var finish = function () {
      if (!isOpen()) {
        modal.hidden = true;
        modal.classList.remove('is-closing');
      }
      card.removeEventListener('transitionend', onEnd);
    };
    var onEnd = function (event) {
      if (event.target === card && event.propertyName === 'opacity') finish();
    };
    card.addEventListener('transitionend', onEnd);
    closeTimer = setTimeout(finish, 420);

    if (lastTrigger && document.contains(lastTrigger)) {
      lastTrigger.focus({ preventScroll: true });
    }
  }

  /* ---------------------------------------------------------------------------
     Wiring
     --------------------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-bm-wl-open]');
    if (opener) {
      event.preventDefault();
      open(opener);
      return;
    }
    if (event.target.closest('[data-bm-wl-close]')) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!isOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    var items = Array.prototype.filter.call(
      card.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
    if (!items.length) return;

    var first = items[0];
    var last  = items[items.length - 1];

    if (event.shiftKey && (document.activeElement === first || document.activeElement === card)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Remember that the next page load belongs to this form, so a success that
     comes back from Shopify can be attributed to the pop-up rather than to the
     hero or the footer — `form.posted_successfully?` is true for every customer
     form on the page, not just the one that posted. */
  var modalForm = modal.querySelector('form[data-bm-newsletter]');
  if (modalForm) {
    modalForm.addEventListener('submit', function () {
      if (modalForm.checkValidity()) writeFlag(PENDING, true);
    });
  }

  /* ---------------------------------------------------------------------------
     When it raises itself
     --------------------------------------------------------------------------- */
  var confirmed = Boolean(modal.querySelector('[data-bm-form-confirmed="true"]'));

  if (confirmed && readFlag(PENDING)) {
    /* They just submitted from here. Show them the answer. */
    writeFlag(PENDING, false);
    open(visibleTrigger());

  } else if (modal.dataset.auto === 'true' && !readFlag(DISMISSED) && !readFlag(SUBSCRIBED) && !confirmed) {
    var delay = parseInt(modal.dataset.delay, 10);
    if (isNaN(delay) || delay < 0) delay = 1000;

    setTimeout(function () {
      if (!isOpen()) open(visibleTrigger());
    }, delay);
  }
})();
